import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Nav, Tab } from "react-bootstrap";
import { handleError, handleSuccess } from "../CommonJquery/CommonJquery";
import MasterLayout from "../masterLayout/MasterLayout";
import {
  connect_facebook_account,
  create_facebook_post,
  delete_facebook_comment,
  delete_facebook_media,
  delete_facebook_post,
  get_facebook_comments,
  get_facebook_insights,
  get_facebook_media,
  get_facebook_page_details,
  get_facebook_posts,
  get_facebook_reviews,
  hide_facebook_comment,
  reply_facebook_comment,
  reply_facebook_review,
  save_facebook_page,
  server_post_data,
  update_facebook_post,
  upload_facebook_media,
} from "../ServiceConnection/serviceconnection";
import Loader from "./Loader";
import "../components/CSS/gmb-premium.css";
import "../components/CSS/facebook-premium.css";

/* ---------------------------------------------------------------------------
   Static config
--------------------------------------------------------------------------- */

const SOCIAL_PROFILES = [
  { key: "google", label: "Google Business", icon: "mdi:google", ready: false },
  { key: "facebook", label: "Facebook", icon: "mdi:facebook", ready: true },
  { key: "instagram", label: "Instagram", icon: "mdi:instagram", ready: false },
  { key: "twitter", label: "Twitter", icon: "ri:twitter-x-fill", ready: false },
  { key: "linkedin", label: "LinkedIn", icon: "mdi:linkedin", ready: false },
  { key: "pinterest", label: "Pinterest", icon: "mdi:pinterest", ready: false },
];

const CHANNEL_TABS = [
  { key: "facebook", label: "Facebook", ready: true },
  { key: "google", label: "Google", ready: false },
  { key: "instagram", label: "Instagram", ready: false },
  { key: "linkedin", label: "LinkedIn", ready: false },
  { key: "twitter", label: "Twitter", ready: false },
  { key: "pinterest", label: "Pinterest", ready: false },
];

const POST_TYPES = [
  { key: "TEXT", label: "Text" },
  { key: "LINK", label: "Link" },
  { key: "PHOTO", label: "Photo" },
  { key: "VIDEO", label: "Video" },
];

const MEDIA_TYPES = [
  { key: "PHOTO", label: "Image Post" },
  { key: "VIDEO", label: "Video Post" },
];

const TIPS = [
  {
    title: "Post Consistently",
    body: "Pages that publish 3–5 times a week see noticeably higher reach than pages that post occasionally.",
  },
  {
    title: "Lead With Visuals",
    body: "Posts with an image or short video get significantly more engagement than text-only updates.",
  },
  {
    title: "Reply To Comments",
    body: "Responding within the first hour boosts distribution and builds trust with your audience.",
  },
];

const emptyPost = {
  post_id: "",
  post_type: "PHOTO",
  message: "",
  link_url: "",
  image_url: "",
  video_url: "",
  video_title: "",
  schedule_date: "",
  schedule_time: "",
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n) =>
  new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

/* ------------------------------------------------------------------------ */

function FacebookPanel() {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("page");

  // page
  const [page, setPage] = useState(null);
  const [pageList, setPageList] = useState([]);
  const [showPagePicker, setShowPagePicker] = useState(false);

  // posts
  const [posts, setPosts] = useState([]);
  const [postStatus, setPostStatus] = useState("published");
  const [showPostModal, setShowPostModal] = useState(false);
  const [postForm, setPostForm] = useState(emptyPost);
  const [mediaFile, setMediaFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [scheduleOn, setScheduleOn] = useState(false);
  const [channel, setChannel] = useState("facebook");
  const [confirmDelete, setConfirmDelete] = useState(null);

  // comments
  const [showComments, setShowComments] = useState(false);
  const [commentPost, setCommentPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [replyBox, setReplyBox] = useState({});

  // reviews
  const [reviews, setReviews] = useState([]);
  const [reviewReply, setReviewReply] = useState({});

  // insights
  const [range, setRange] = useState({
    start_date: daysAgoISO(29),
    end_date: todayISO(),
    period: "day",
  });
  const [insights, setInsights] = useState(null);

  // media
  const [media, setMedia] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState("");
  const [uploadCaption, setUploadCaption] = useState("");

  const clickRef = useRef(0);

  /* ------------------------- common caller ------------------------- */
  const call = useCallback(async (url, fields = {}, files = {}) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") fd.append(k, v);
    });
    Object.entries(files).forEach(([k, f]) => f && fd.append(k, f));

    try {
      const res = await server_post_data(url, fd);
      const parts = res.message.split("~@~");
      if (parseInt(parts[0]) === 1) {
        handleError(parts[1]);
        return null;
      }
      const parsed = JSON.parse(parts[1]);
      if (parsed.error) {
        handleError(parsed.message || "Facebook API error");
        return null;
      }
      return parsed;
    } catch (e) {
      handleError("network");
      return null;
    }
  }, []);

  /* ------------------------- loaders ------------------------- */
  const loadPage = useCallback(async () => {
    setLoading(true);
    const d = await call(get_facebook_page_details);
    console.log(d);
    if (d?.page) setPage(d.page);
    setLoading(false);
  }, [call]);

  const loadPosts = useCallback(
    async (status = postStatus) => {
      setLoading(true);
      const d = await call(get_facebook_posts, { status, limit: 50 });
      setPosts(d?.posts || []);
      setLoading(false);
    },
    [call, postStatus],
  );

  const loadReviews = useCallback(async () => {
    setLoading(true);
    const d = await call(get_facebook_reviews, { limit: 50 });
    setReviews(d?.reviews || []);
    setLoading(false);
  }, [call]);

  const loadInsights = useCallback(async () => {
    setLoading(true);
    const d = await call(get_facebook_insights, range);
    setInsights(d?.insights || null);
    setLoading(false);
  }, [call, range]);

  const loadMedia = useCallback(async () => {
    setLoading(true);
    const d = await call(get_facebook_media, { limit: 60 });
    setMedia(d?.media || []);
    setLoading(false);
  }, [call]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (activeTab === "posts") loadPosts();
    if (activeTab === "reviews") loadReviews();
    if (activeTab === "insights") loadInsights();
    if (activeTab === "media") loadMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  /* ------------------------- connect flow ------------------------- */

  // Actual async work — FB SDK ka callback async function accept nahi karta,
  // isliye ise alag rakha hai.
  const afterFbLogin = useCallback(
    async (accessToken) => {
      setLoading(true);
      const d = await call(connect_facebook_account, {
        user_access_token: accessToken,
      });
      setLoading(false);

      if (d?.pages?.length) {
        setPageList(
          d.pages.map((p) => ({ ...p, userToken: d.longLivedUserToken })),
        );
        setShowPagePicker(true);
      } else {
        handleError("No manageable Page was found on this account");
      }
    },
    [call],
  );

  const handleConnect = () => {
    if (window.location.protocol !== "https:") {
      handleError(
        "Facebook Login requires HTTPS. Please open this app over https://",
      );
      return;
    }

    if (!window.FB || typeof window.FB.login !== "function") {
      handleError("Facebook SDK is not ready yet. Please refresh the page.");
      return;
    }

    // NOTE: callback MUST be a plain (non-async) function.
    window.FB.login(
      function (resp) {
        const token =
          resp && resp.authResponse && resp.authResponse.accessToken;
        if (!token) {
          handleError("Facebook login was cancelled");
          return;
        }
        afterFbLogin(token);
      },
      {
        scope:
          "pages_show_list,pages_manage_posts,pages_read_engagement," +
          "pages_manage_engagement,pages_read_user_content,read_insights,business_management",
        return_scopes: true,
      },
    );
  };

  const handleSelectPage = async (p) => {
    setLoading(true);
    const d = await call(save_facebook_page, {
      page_id: p.pageId,
      page_access_token: p.accessToken,
      page_name: p.name,
      page_url: p.link,
      user_access_token: p.userToken,
    });
    setLoading(false);
    if (d) {
      handleSuccess("Facebook Page connected successfully");
      setShowPagePicker(false);
      loadPage();
    }
  };

  /* ------------------------- post actions ------------------------- */
  const resetComposer = () => {
    setPostForm(emptyPost);
    setMediaFile(null);
    setFilePreview("");
    setScheduleOn(false);
    setChannel("facebook");
  };

  const openCreate = () => {
    resetComposer();
    setShowPostModal(true);
  };

  const openEdit = (p) => {
    setPostForm({
      ...emptyPost,
      post_id: p.postId,
      message: p.message,
      post_type: "TEXT",
    });
    setMediaFile(null);
    setFilePreview(p.image || "");
    setScheduleOn(false);
    setChannel("facebook");
    setShowPostModal(true);
  };

  const onPickFile = (file) => {
    if (!file) return;
    setMediaFile(file);
    setFilePreview(URL.createObjectURL(file));
  };

  const clearFile = () => {
    setMediaFile(null);
    setFilePreview("");
  };

  const savePost = async () => {
    if (!postForm.message.trim()) {
      handleError("Post content is required");
      return;
    }
    if (clickRef.current === 1) return;
    clickRef.current = 1;
    setLoading(true);

    const payload = { ...postForm };
    if (!scheduleOn) {
      payload.schedule_date = "";
      payload.schedule_time = "";
    }

    const d = postForm.post_id
      ? await call(update_facebook_post, {
          post_id: postForm.post_id,
          message: postForm.message,
          schedule_date: payload.schedule_date,
          schedule_time: payload.schedule_time,
        })
      : await call(create_facebook_post, payload, { media_file: mediaFile });

    setLoading(false);
    clickRef.current = 0;
    if (d) {
      handleSuccess(
        postForm.post_id
          ? "Post updated successfully"
          : "Post created successfully",
      );
      setShowPostModal(false);
      loadPosts();
    }
  };

  const removePost = async () => {
    setLoading(true);
    const d = await call(delete_facebook_post, { post_id: confirmDelete });
    setLoading(false);
    setConfirmDelete(null);
    if (d) {
      handleSuccess("Post deleted successfully");
      loadPosts();
    }
  };

  /* ------------------------- comments ------------------------- */
  const openComments = async (p) => {
    setCommentPost(p);
    setShowComments(true);
    setLoading(true);
    const d = await call(get_facebook_comments, {
      post_id: p.postId,
      limit: 50,
    });
    setComments(d?.comments || []);
    setLoading(false);
  };

  const sendReply = async (objectId) => {
    const msg = (replyBox[objectId] || "").trim();
    if (!msg) return;
    setLoading(true);
    const d = await call(reply_facebook_comment, {
      object_id: objectId,
      message: msg,
    });
    setLoading(false);
    if (d) {
      handleSuccess("Reply posted successfully");
      setReplyBox((s) => ({ ...s, [objectId]: "" }));
      openComments(commentPost);
    }
  };

  const removeComment = async (id) => {
    setLoading(true);
    const d = await call(delete_facebook_comment, { comment_id: id });
    setLoading(false);
    if (d) {
      handleSuccess("Comment deleted successfully");
      openComments(commentPost);
    }
  };

  const toggleHide = async (c) => {
    setLoading(true);
    const d = await call(hide_facebook_comment, {
      comment_id: c.id,
      is_hidden: c.is_hidden ? "0" : "1",
    });
    setLoading(false);
    if (d) {
      handleSuccess(c.is_hidden ? "Comment unhidden" : "Comment hidden");
      openComments(commentPost);
    }
  };

  /* ------------------------- reviews ------------------------- */
  const sendReviewReply = async (storyId) => {
    const msg = (reviewReply[storyId] || "").trim();
    if (!msg) return;
    setLoading(true);
    const d = await call(reply_facebook_review, {
      story_id: storyId,
      reply_text: msg,
    });
    setLoading(false);
    if (d) {
      handleSuccess("Reply posted successfully");
      setReviewReply((s) => ({ ...s, [storyId]: "" }));
      loadReviews();
    }
  };

  /* ------------------------- media ------------------------- */
  const doUpload = async () => {
    if (!uploadFile) {
      handleError("Please select a photo first");
      return;
    }
    setLoading(true);
    const d = await call(
      upload_facebook_media,
      { caption: uploadCaption, publish: "1" },
      { media_file: uploadFile },
    );
    setLoading(false);
    if (d) {
      handleSuccess("Media uploaded successfully");
      setShowUpload(false);
      setUploadFile(null);
      setUploadPreview("");
      setUploadCaption("");
      loadMedia();
    }
  };

  const removeMedia = async (id) => {
    setLoading(true);
    const d = await call(delete_facebook_media, { media_id: id });
    setLoading(false);
    if (d) {
      handleSuccess("Media deleted successfully");
      loadMedia();
    }
  };

  /* ------------------------- helpers ------------------------- */
  const initials = (n) => (n || "?").trim().charAt(0).toUpperCase();

  const fmtDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "-";

  const isEdit = !!postForm.post_id;
  const isMediaType = ["PHOTO", "VIDEO"].includes(postForm.post_type);

  return (
    <MasterLayout>
      <div className="gmb-premium fb-premium">
        {loading && <Loader />}

        {/* ------------------------- Topbar ------------------------- */}
        <div className="gmb-topbar">
          <div className="gmb-topbar-title">
            <h5 className="gmb-display">Facebook Page</h5>
            <span className="gmb-royal-badge">
              <Icon icon="mdi:facebook" /> Social Suite
            </span>
            {page && (
              <span className="gmb-live">
                <span className="gmb-live-dot" /> Live
              </span>
            )}
          </div>
          <div className="gmb-topbar-actions">
            <button
              className="btn btn-outline-primary-600"
              onClick={handleConnect}
            >
              <Icon icon="mdi:account-switch-outline" /> Switch Page
            </button>
            <button className="btn btn-primary-600" onClick={openCreate}>
              <Icon icon="ic:baseline-plus" /> Create Post
            </button>
          </div>
        </div>

        {/* ------------------------- Page hero ------------------------- */}
        {page && (
          <div className="fb-page-hero">
            <img
              src={page.picture?.data?.url || ""}
              alt={page.name}
              onError={(e) => (e.currentTarget.style.visibility = "hidden")}
            />
            <div>
              <h6>{page.name}</h6>
              <span>{page.category || "Facebook Page"}</span>
            </div>
            <div className="fb-page-stats">
              <div>
                <b className="gmb-mono">{page.fan_count ?? 0}</b>
                <small>Likes</small>
              </div>
              <div>
                <b className="gmb-mono">{page.followers_count ?? 0}</b>
                <small>Followers</small>
              </div>
              <div>
                <b className="gmb-mono">
                  {page.overall_star_rating
                    ? page.overall_star_rating.toFixed(1)
                    : "-"}
                </b>
                <small>Rating</small>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------- Tabs ------------------------- */}
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav className="nav-tabs">
            <Nav.Item>
              <Nav.Link eventKey="page">
                <Icon icon="mdi:information-outline" /> Page
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="posts">
                <Icon icon="mdi:post-outline" /> Posts
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="reviews">
                <Icon icon="mdi:star-outline" /> Reviews
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="insights">
                <Icon icon="mdi:chart-line" /> Insights
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="media">
                <Icon icon="mdi:image-multiple-outline" /> Media
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* ------------------ PAGE ------------------ */}
            <Tab.Pane eventKey="page">
              {!page ? (
                <div className="gmb-empty">
                  No Facebook Page is connected yet — use “Switch Page” above to
                  connect one.
                </div>
              ) : (
                <div className="gmb-card" style={{ padding: 20 }}>
                  <div className="gmb-detail-row">
                    <strong>Name</strong>
                    <span>{page.name}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Category</strong>
                    <span>{page.category || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>About</strong>
                    <span>{page.about || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Phone</strong>
                    <span>{page.phone || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Website</strong>
                    <span>{page.website || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Address</strong>
                    <span>{page.single_line_address || "-"}</span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Page Link</strong>
                    <span>
                      {page.link ? (
                        <a href={page.link} target="_blank" rel="noreferrer">
                          {page.link}
                        </a>
                      ) : (
                        "-"
                      )}
                    </span>
                  </div>
                  <div className="gmb-detail-row">
                    <strong>Verified</strong>
                    <span>{page.verification_status || "-"}</span>
                  </div>
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ POSTS ------------------ */}
            <Tab.Pane eventKey="posts">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="gmb-segmented">
                  {["published", "scheduled"].map((s) => (
                    <button
                      key={s}
                      className={postStatus === s ? "active" : ""}
                      onClick={() => {
                        setPostStatus(s);
                        loadPosts(s);
                      }}
                    >
                      {s === "published" ? "Published" : "Scheduled"}
                    </button>
                  ))}
                </div>
                <button
                  className="btn btn-outline-primary-600 btn-sm"
                  onClick={() => loadPosts()}
                >
                  <Icon icon="mdi:refresh" /> Refresh
                </button>
              </div>

              {posts.length === 0 ? (
                <div className="gmb-empty">No posts found yet.</div>
              ) : (
                <div className="row g-3">
                  {posts.map((p) => (
                    <div className="col-xl-4 col-lg-6" key={p.postId}>
                      <div className="gmb-card gmb-post-card">
                        <span
                          className={`gmb-post-badge ${
                            p.isPublished ? "standard" : "fb-scheduled-chip"
                          }`}
                        >
                          {p.isPublished ? p.statusType || "Post" : "Scheduled"}
                        </span>
                        {p.image && (
                          <img
                            className="gmb-post-image"
                            src={p.image}
                            alt=""
                          />
                        )}
                        <p className="gmb-post-summary">
                          {p.message || "(no text)"}
                        </p>
                        <div className="gmb-metric-range">
                          <span>
                            {fmtDate(p.createdTime || p.scheduledPublishTime)}
                          </span>
                          {p.permalink && (
                            <a
                              className="gmb-competitor-maps-link"
                              href={p.permalink}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View
                            </a>
                          )}
                        </div>
                        <div className="fb-post-engagement">
                          <span>
                            <Icon icon="mdi:thumb-up-outline" /> {p.likeCount}
                          </span>
                          <span>
                            <Icon icon="mdi:comment-outline" /> {p.commentCount}
                          </span>
                          <span>
                            <Icon icon="mdi:share-outline" /> {p.shareCount}
                          </span>
                        </div>
                        <div className="d-flex gap-2 mt-3">
                          <button
                            className="btn btn-outline-primary-600 btn-sm"
                            onClick={() => openComments(p)}
                          >
                            <Icon icon="mdi:comment-text-outline" /> Comments
                          </button>
                          <button
                            className="btn btn-outline-primary-600 btn-sm"
                            onClick={() => openEdit(p)}
                          >
                            <Icon icon="lucide:edit" />
                          </button>
                          <button
                            className="btn btn-outline-danger-600 btn-sm"
                            onClick={() => setConfirmDelete(p.postId)}
                          >
                            <Icon icon="fluent:delete-24-regular" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ REVIEWS ------------------ */}
            <Tab.Pane eventKey="reviews">
              {reviews.length === 0 ? (
                <div className="gmb-empty">No recommendations found.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {reviews.map((r, i) => (
                    <div
                      className="gmb-card gmb-review-card"
                      key={r.storyId || i}
                    >
                      <div className="gmb-review-head">
                        <div>
                          <div className="gmb-review-author">
                            {r.reviewerName}
                          </div>
                          <div className="gmb-rating">
                            <span
                              className={
                                r.recommendationType === "positive"
                                  ? ""
                                  : "gmb-rating-stars-empty"
                              }
                            >
                              {r.recommendationType === "positive"
                                ? "👍 Recommends"
                                : "👎 Doesn't recommend"}
                            </span>
                          </div>
                        </div>
                        <span className="gmb-metric-label">
                          {fmtDate(r.createdTime)}
                        </span>
                      </div>
                      {r.reviewText && (
                        <p className="gmb-review-comment">{r.reviewText}</p>
                      )}
                      {r.storyId && (
                        <div className="d-flex gap-2 mt-3">
                          <input
                            className="form-control"
                            placeholder="Write a reply..."
                            value={reviewReply[r.storyId] || ""}
                            onChange={(e) =>
                              setReviewReply((s) => ({
                                ...s,
                                [r.storyId]: e.target.value,
                              }))
                            }
                          />
                          <button
                            className="btn btn-primary-600"
                            onClick={() => sendReviewReply(r.storyId)}
                          >
                            Send
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ INSIGHTS ------------------ */}
            <Tab.Pane eventKey="insights">
              <div className="gmb-date-row">
                <div>
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.start_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, start_date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={range.end_date}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, end_date: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label>Period</label>
                  <select
                    className="form-control"
                    value={range.period}
                    onChange={(e) =>
                      setRange((s) => ({ ...s, period: e.target.value }))
                    }
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="days_28">28 Days</option>
                  </select>
                </div>
                <button className="btn btn-primary-600" onClick={loadInsights}>
                  <Icon icon="mdi:magnify" /> Fetch
                </button>
              </div>

              {!insights ? (
                <div className="gmb-empty">
                  Select a date range and click Fetch to load metrics.
                </div>
              ) : (
                <div className="row g-3">
                  {Object.entries(insights).map(([key, m]) => (
                    <div className="col-xl-3 col-lg-4 col-md-6" key={key}>
                      <div className="gmb-card gmb-metric-card">
                        <div
                          className="gmb-metric-icon"
                          style={{
                            background: "var(--info-50)",
                            color: "var(--facebook)",
                          }}
                        >
                          <Icon icon="mdi:chart-box-outline" />
                        </div>
                        <div className="gmb-metric-label">{m.title || key}</div>
                        <div className="gmb-metric-total gmb-mono">
                          {m.total}
                        </div>
                        <div className="gmb-metric-range">
                          <span>{range.start_date}</span>
                          <span>{range.end_date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>

            {/* ------------------ MEDIA ------------------ */}
            <Tab.Pane eventKey="media">
              <div className="d-flex justify-content-end mb-3">
                <button
                  className="btn btn-primary-600"
                  onClick={() => setShowUpload(true)}
                >
                  <Icon icon="ic:baseline-upload" /> Upload Photo
                </button>
              </div>
              {media.length === 0 ? (
                <div className="gmb-empty">No media found.</div>
              ) : (
                <div className="row g-3">
                  {media.map((m) => (
                    <div
                      className="col-xl-2 col-lg-3 col-md-4 col-6"
                      key={m.mediaId}
                    >
                      <div className="gmb-media-tile">
                        <img
                          src={m.thumbnailUrl || m.fullUrl}
                          alt={m.caption}
                        />
                        <div className="gmb-media-actions">
                          <a
                            className="gmb-media-action-btn"
                            href={m.fullUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Icon icon="mdi:eye-outline" />
                          </a>
                          <button
                            className="gmb-media-action-btn danger"
                            onClick={() => removeMedia(m.mediaId)}
                          >
                            <Icon icon="fluent:delete-24-regular" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* ==================== COMPOSER MODAL ==================== */}
        <Modal
          className="gmb-modal fb-composer-modal"
          size="xl"
          centered
          show={showPostModal}
          onHide={() => setShowPostModal(false)}
        >
          <Modal.Body>
            <div className="fb-composer">
              {/* ---------- LEFT: social profiles ---------- */}
              <div className="fb-composer-col fb-composer-rail">
                <div className="fb-composer-heading">
                  Select Social Profiles
                </div>

                {SOCIAL_PROFILES.map((sp) => {
                  const connected = sp.key === "facebook" && !!page;
                  return (
                    <div
                      key={sp.key}
                      className={`fb-social-item ${sp.key} ${
                        connected ? "is-connected" : ""
                      }`}
                    >
                      <div className="fb-social-head">
                        <Icon icon={sp.icon} />
                        {sp.label}
                        {connected && (
                          <span className="fb-social-count">(1)</span>
                        )}
                      </div>
                      <div className="fb-social-foot">
                        <button
                          type="button"
                          disabled={!sp.ready}
                          onClick={sp.ready ? handleConnect : undefined}
                        >
                          {connected ? "Connected" : "Connect Account"}
                        </button>
                        <span className="fb-social-status">
                          <Icon
                            icon={
                              connected
                                ? "mdi:check-circle"
                                : "mdi:check-circle-outline"
                            }
                          />
                        </span>
                      </div>
                    </div>
                  );
                })}

                <div className="fb-rail-divider" />

                <div className="fb-toggle-row">
                  <span>Schedule for Later</span>
                  <button
                    type="button"
                    className={`fb-switch ${scheduleOn ? "on" : ""}`}
                    onClick={() => setScheduleOn((v) => !v)}
                    aria-label="Toggle scheduling"
                  />
                </div>

                {scheduleOn && (
                  <div className="row g-2 mt-3">
                    <div className="col-12">
                      <label className="gmb-field-label">Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={postForm.schedule_date}
                        onChange={(e) =>
                          setPostForm((s) => ({
                            ...s,
                            schedule_date: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="col-12">
                      <label className="gmb-field-label">Time</label>
                      <input
                        type="time"
                        className="form-control"
                        value={postForm.schedule_time}
                        onChange={(e) =>
                          setPostForm((s) => ({
                            ...s,
                            schedule_time: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="gmb-char-count">
                      Pick a time at least 10 minutes from now.
                    </div>
                  </div>
                )}
              </div>

              {/* ---------- MIDDLE: editor ---------- */}
              <div className="fb-composer-col">
                <div className="fb-composer-title">
                  {isEdit ? "Edit Post" : "Create Post"}
                </div>
                <div className="fb-composer-sub">
                  {page ? `${page.name} selected` : "No page selected"}
                </div>

                <div className="fb-channel-tabs">
                  {CHANNEL_TABS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      disabled={!c.ready}
                      className={channel === c.key ? "active" : ""}
                      onClick={() => c.ready && setChannel(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>

                <div className="fb-account-row">
                  <Icon
                    icon="mdi:facebook"
                    style={{ color: "var(--facebook)" }}
                  />
                  Facebook Page:
                  <span className="fb-account-chip">
                    {page ? page.name : "Not connected"}
                  </span>
                </div>

                {!isEdit && (
                  <>
                    <div className="fb-field-title">Select Post Type</div>
                    <div className="fb-pills">
                      {POST_TYPES.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          className={`fb-pill ${
                            postForm.post_type === t.key ? "active" : ""
                          }`}
                          onClick={() =>
                            setPostForm((s) => ({ ...s, post_type: t.key }))
                          }
                        >
                          {postForm.post_type === t.key && (
                            <Icon icon="mdi:check" />
                          )}
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {!isEdit && isMediaType && (
                  <>
                    <div className="fb-field-title">Select Media Type</div>
                    <div className="fb-pills">
                      {MEDIA_TYPES.map((t) => (
                        <button
                          key={t.key}
                          type="button"
                          className={`fb-pill ${
                            postForm.post_type === t.key ? "active" : ""
                          }`}
                          onClick={() => {
                            setPostForm((s) => ({ ...s, post_type: t.key }));
                            clearFile();
                          }}
                        >
                          {postForm.post_type === t.key && (
                            <Icon icon="mdi:check" />
                          )}
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {!isEdit && postForm.post_type === "VIDEO" && (
                  <div className="mb-3">
                    <div className="fb-field-title">Video Title</div>
                    <input
                      className="form-control"
                      placeholder="Eg: Store Walkthrough, Customer Story"
                      value={postForm.video_title}
                      onChange={(e) =>
                        setPostForm((s) => ({
                          ...s,
                          video_title: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}

                {!isEdit && postForm.post_type === "LINK" && (
                  <div className="mb-3">
                    <div className="fb-field-title">Link URL</div>
                    <input
                      className="form-control"
                      placeholder="https://example.com"
                      value={postForm.link_url}
                      onChange={(e) =>
                        setPostForm((s) => ({ ...s, link_url: e.target.value }))
                      }
                    />
                  </div>
                )}

                {!isEdit && isMediaType && (
                  <>
                    <div className="fb-field-title">
                      Add Post{" "}
                      {postForm.post_type === "VIDEO" ? "Video" : "Image"} (
                      Recommended Size - 1200 X 900 )
                    </div>

                    {filePreview ? (
                      <div className="fb-drop-preview">
                        {postForm.post_type === "VIDEO" ? (
                          <video src={filePreview} controls />
                        ) : (
                          <img src={filePreview} alt="preview" />
                        )}
                        <button
                          type="button"
                          className="fb-drop-remove"
                          onClick={clearFile}
                        >
                          <Icon icon="mdi:close" />
                        </button>
                      </div>
                    ) : (
                      <label className="fb-dropzone">
                        <input
                          type="file"
                          accept={
                            postForm.post_type === "VIDEO"
                              ? "video/*"
                              : "image/*"
                          }
                          onChange={(e) => onPickFile(e.target.files[0])}
                        />
                        <div className="fb-drop-icon">
                          <Icon icon="mdi:image-plus-outline" />
                        </div>
                        <div className="fb-drop-title">
                          Upload or Drag{" "}
                          {postForm.post_type === "VIDEO" ? "Video" : "Photo"}
                        </div>
                        <div className="fb-drop-hint">
                          Maximum Size - 35 MB. Format JPG, PNG, MP4 &amp; other
                          media formats
                        </div>
                      </label>
                    )}

                    <div className="fb-url-note">
                      Or paste a public media URL
                    </div>
                    <input
                      className="form-control"
                      placeholder={
                        postForm.post_type === "VIDEO"
                          ? "https://example.com/video.mp4"
                          : "https://example.com/image.jpg"
                      }
                      value={
                        postForm.post_type === "VIDEO"
                          ? postForm.video_url
                          : postForm.image_url
                      }
                      onChange={(e) =>
                        setPostForm((s) =>
                          postForm.post_type === "VIDEO"
                            ? { ...s, video_url: e.target.value }
                            : { ...s, image_url: e.target.value },
                        )
                      }
                    />
                  </>
                )}

                <div className="fb-field-title mt-4">Write a Post</div>
                <textarea
                  className="form-control"
                  rows={5}
                  placeholder="Eg: New Product Launch, Happy Customer Post"
                  value={postForm.message}
                  onChange={(e) =>
                    setPostForm((s) => ({ ...s, message: e.target.value }))
                  }
                />
                <div className="gmb-char-count">
                  {postForm.message.length} characters
                </div>

                <div className="fb-submit-bar">
                  <button
                    type="button"
                    className="btn btn-outline-primary-600"
                    onClick={() => setShowPostModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="fb-submit"
                    disabled={!page}
                    onClick={savePost}
                  >
                    <Icon icon="mdi:facebook" />
                    {isEdit ? "Update Post" : "Create Post For Facebook"}
                  </button>
                </div>
              </div>

              {/* ---------- RIGHT: preview + tips ---------- */}
              <div className="fb-composer-col fb-composer-aside">
                <div className="fb-preview">
                  <div className="fb-preview-head">
                    <Icon
                      icon="mdi:facebook"
                      style={{ color: "var(--facebook)" }}
                    />
                    Facebook Post Preview
                  </div>
                  {filePreview || postForm.image_url ? (
                    <img
                      className="fb-preview-img"
                      src={filePreview || postForm.image_url}
                      alt=""
                    />
                  ) : (
                    <div className="fb-preview-empty">
                      <Icon icon="mdi:image-outline" style={{ fontSize: 34 }} />
                      No Image Added
                    </div>
                  )}
                  <div className="fb-preview-body">
                    <div className="fb-preview-title">
                      {postForm.video_title || "No Title Added"}
                    </div>
                    <p>{postForm.message || "Your content here"}</p>
                    {postForm.link_url && (
                      <span className="fb-preview-cta">
                        <Icon icon="mdi:open-in-new" /> Learn More
                      </span>
                    )}
                  </div>
                </div>

                <div className="fb-composer-heading">Tips to Improve Reach</div>
                {TIPS.map((t) => (
                  <div className="fb-tip-card" key={t.title}>
                    <div className="fb-tip-icon">
                      <Icon icon="mdi:lightbulb-on-outline" />
                    </div>
                    <h6>{t.title}</h6>
                    <p>{t.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* ==================== COMMENTS MODAL ==================== */}
        <Modal
          className="gmb-modal"
          size="lg"
          centered
          show={showComments}
          onHide={() => setShowComments(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Comments</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex gap-2 mb-3">
              <input
                className="form-control"
                placeholder="Write a comment on this post..."
                value={replyBox[commentPost?.postId] || ""}
                onChange={(e) =>
                  setReplyBox((s) => ({
                    ...s,
                    [commentPost.postId]: e.target.value,
                  }))
                }
              />
              <button
                className="btn btn-primary-600"
                onClick={() => sendReply(commentPost.postId)}
              >
                Post
              </button>
            </div>

            {comments.length === 0 ? (
              <div className="gmb-empty">No comments yet.</div>
            ) : (
              comments.map((c) => (
                <div key={c.id}>
                  <div className="fb-comment">
                    <div className="fb-comment-avatar">
                      {initials(c.from?.name)}
                    </div>
                    <div className="fb-comment-body">
                      <b>{c.from?.name || "Facebook User"}</b>
                      <p>{c.message}</p>
                      <div className="fb-comment-actions">
                        <button
                          onClick={() =>
                            setReplyBox((s) => ({
                              ...s,
                              [c.id]: s[c.id] || "",
                            }))
                          }
                        >
                          Reply
                        </button>
                        <button onClick={() => toggleHide(c)}>
                          {c.is_hidden ? "Unhide" : "Hide"}
                        </button>
                        <button
                          className="danger"
                          onClick={() => removeComment(c.id)}
                        >
                          Delete
                        </button>
                      </div>
                      {replyBox[c.id] !== undefined && (
                        <div className="d-flex gap-2 mt-2">
                          <input
                            className="form-control"
                            placeholder="Write a reply..."
                            value={replyBox[c.id]}
                            onChange={(e) =>
                              setReplyBox((s) => ({
                                ...s,
                                [c.id]: e.target.value,
                              }))
                            }
                          />
                          <button
                            className="btn btn-primary-600 btn-sm"
                            onClick={() => sendReply(c.id)}
                          >
                            Send
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {(c.comments?.data || []).map((r) => (
                    <div className="fb-comment fb-comment-reply" key={r.id}>
                      <div className="fb-comment-avatar">
                        {initials(r.from?.name)}
                      </div>
                      <div className="fb-comment-body">
                        <b>{r.from?.name}</b>
                        <p>{r.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </Modal.Body>
        </Modal>

        {/* ==================== PAGE PICKER ==================== */}
        <Modal
          className="gmb-modal"
          centered
          show={showPagePicker}
          onHide={() => setShowPagePicker(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Select a Page</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {pageList.map((p) => (
              <div
                className="gmb-card gmb-location-card mb-2 d-flex align-items-center gap-3"
                key={p.pageId}
                style={{ cursor: "pointer" }}
                onClick={() => handleSelectPage(p)}
              >
                <img
                  src={p.picture}
                  alt=""
                  style={{ width: 42, height: 42, borderRadius: "50%" }}
                />
                <div>
                  <h6 className="mb-0">{p.name}</h6>
                  <div className="gmb-location-meta">
                    {p.category} · {p.fanCount} likes
                  </div>
                </div>
              </div>
            ))}
          </Modal.Body>
        </Modal>

        {/* ==================== UPLOAD MEDIA ==================== */}
        <Modal
          className="gmb-modal"
          centered
          show={showUpload}
          onHide={() => setShowUpload(false)}
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>Upload Photo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <label className="fb-dropzone">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files[0];
                  setUploadFile(f);
                  if (f) setUploadPreview(URL.createObjectURL(f));
                }}
              />
              <div className="fb-drop-icon">
                <Icon icon="mdi:image-plus-outline" />
              </div>
              <div className="fb-drop-title">Upload or Drag Photo</div>
              <div className="fb-drop-hint">Maximum Size - 35 MB. JPG, PNG</div>
            </label>
            {uploadPreview && (
              <img className="gmb-upload-preview" src={uploadPreview} alt="" />
            )}
            <div className="mt-3">
              <label className="gmb-field-label">Caption</label>
              <input
                className="form-control"
                value={uploadCaption}
                onChange={(e) => setUploadCaption(e.target.value)}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setShowUpload(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary-600" onClick={doUpload}>
              Upload
            </button>
          </Modal.Footer>
        </Modal>

        {/* ==================== DELETE CONFIRM ==================== */}
        <Modal
          className="gmb-modal"
          size="sm"
          centered
          show={!!confirmDelete}
          onHide={() => setConfirmDelete(null)}
        >
          <Modal.Body className="text-center">
            <Icon
              icon="fluent:delete-24-regular"
              style={{ fontSize: 34, color: "var(--danger-main)" }}
            />
            <p className="mt-2 mb-0">
              Please confirm, do you want to delete this post?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline-primary-600"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button className="btn btn-danger-600" onClick={removePost}>
              Confirm
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </MasterLayout>
  );
}

export default FacebookPanel;
